import { useMemo, useState } from 'react';
import {
  assemblyPctNum,
  isOverdue,
  isOverviewHold,
  parseDeliveryDate,
  type OverviewVessel,
} from '@technohull/shared';
import { useOverview } from '../features/hooks.js';
import { printSchedule } from '../features/exportSchedule.js';
import { Chip, ChipGroup, ErrorState, Fact, FactEnd, FactSentence, InlineError, LoadingState, PageHeader, Pill, SearchInput, SyncLabel, type FactTone } from '../components/ui.js';
import { useFormatDate } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';
import { IconAlertTriangle, IconCheck, IconPrinter } from '../components/icons.js';

type Panel = 'holds' | 'overdue' | 'departing' | 'anf';
const PANEL_LABEL: Record<Panel, string> = { holds: 'Holds', overdue: 'Overdue', departing: 'Departing', anf: 'AnF' };
const esc = (s: string) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
const normAnf = (v: OverviewVessel) => String(v.anf ?? '').trim().toLowerCase(); // 'yes' | 'hold' | 'no' | ''
// exact AnF label — ONLY 'Yes'/'Hold' are real AnF states; everything else (No, empty, a checkbox
// value, free text) is not AnF. returns null so the column shows '—' instead of a false 'Hold'.
const anfLabel = (v: OverviewVessel): 'Yes' | 'Hold' | null => {
  const a = normAnf(v);
  return a === 'yes' ? 'Yes' : a === 'hold' ? 'Hold' : null;
};

// vessel alert board — pending holds / overdue / departing / AnF. each category is an independent
// toggle (no baked-in constraints): the departing window is adjustable and AnF lists EVERY vessel
// flagged Yes/Hold in the overview (not only the near-complete ones), for cross-checking with sales.
export function AssemblyAlerts() {
  const { data, isLoading, isFetching, error, refetch } = useOverview();
  const fmt = useFormatDate();
  const isPhone = useIsPhone();
  const [active, setActive] = useState<Set<Panel>>(() => new Set<Panel>(['holds', 'overdue', 'departing', 'anf']));
  const [anfVals, setAnfVals] = useState<Set<'yes' | 'hold'>>(() => new Set(['yes', 'hold']));
  // keep the raw text so the field can be cleared while typing; the window falls back to 14 days
  const [departRaw, setDepartRaw] = useState('14');
  const departDays = Math.min(365, Math.max(1, Number(departRaw) || 14));
  const [q, setQ] = useState('');
  const [printErr, setPrintErr] = useState<Error | null>(null);

  const now = useMemo(() => new Date(), []);
  const vessels = data?.vessels ?? [];

  const upcoming = (v: OverviewVessel): boolean => {
    if (v.left) return false;
    const d = parseDeliveryDate(v);
    if (!d) return false;
    const end = new Date(now);
    end.setUTCDate(end.getUTCDate() + departDays);
    return d >= now && d <= end;
  };
  const anfFlag = (v: OverviewVessel): boolean => {
    if (v.left) return false;
    const l = anfLabel(v);
    return l != null && anfVals.has(l.toLowerCase() as 'yes' | 'hold');
  };
  const flagsOf = (v: OverviewVessel) => ({
    holds: isOverviewHold(v),
    overdue: isOverdue(v, now),
    departing: upcoming(v),
    anf: anfFlag(v),
  });

  // category totals (independent of the active toggles) for the tiles.
  const counts = useMemo(() => {
    const c = { holds: 0, overdue: 0, departing: 0, anf: 0 };
    for (const v of vessels) { const f = flagsOf(v); if (f.holds) c.holds++; if (f.overdue) c.overdue++; if (f.departing) c.departing++; if (f.anf) c.anf++; }
    return c;
  }, [vessels, departDays, anfVals, now]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const query = fold(q.trim());
    return vessels
      .map((v) => ({ v, f: flagsOf(v) }))
      .filter(({ f }) => (['holds', 'overdue', 'departing', 'anf'] as Panel[]).some((p) => active.has(p) && f[p]))
      .filter(({ v }) => hasText(query, `${v.sap} ${v.name} ${v.model}`))
      .sort((a, b) => (a.v.deliveryDate < b.v.deliveryDate ? -1 : 1));
  }, [vessels, active, q, departDays, anfVals, now]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (p: Panel) => setActive((prev) => { const n = new Set(prev); n.has(p) ? n.delete(p) : n.add(p); return n; });
  // the four counts are the headline sentence, and each figure is the category's show / hide toggle (in
  // the colour of its flag pill in the table, so the sentence doubles as the key)
  const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
  const facts: Array<{ p: Panel; text: string; what: string; tone: FactTone }> = [
    { p: 'holds', text: `${counts.holds} pending ${plural(counts.holds, 'hold', 'holds')}`, what: 'boats on hold', tone: 'warn' },
    { p: 'overdue', text: `${counts.overdue} ${plural(counts.overdue, 'boat', 'boats')} overdue`, what: 'overdue boats', tone: 'err' },
    { p: 'departing', text: `${counts.departing} departing within ${departDays} ${plural(departDays, 'day', 'days')}`, what: 'departing boats', tone: 'info' },
    { p: 'anf', text: `${counts.anf} flagged AnF`, what: 'boats flagged AnF (Yes / Hold)', tone: 'acc' },
  ];
  const factOf = ({ p, text, what, tone }: (typeof facts)[number]) => (
    <Fact
      tone={counts[p] ? tone : undefined}
      icon={<span className="al-fact-tick" aria-hidden="true"><IconCheck size={12} /></span>}
      pressed={active.has(p)}
      onClick={() => toggle(p)}
      title={`${active.has(p) ? 'Hide' : 'Show'} ${what} in the list`}
    >{text}</Fact>
  );
  const toggleAnf = (a: 'yes' | 'hold') => setAnfVals((prev) => { const n = new Set(prev); n.has(a) ? n.delete(a) : n.add(a); return n; });

  const flagChips = (f: ReturnType<typeof flagsOf>, v: OverviewVessel) => (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {f.holds && <Pill kind="warn">Hold</Pill>}
      {f.overdue && <Pill kind="bad">Overdue</Pill>}
      {f.departing && <Pill kind="info">Departing</Pill>}
      {f.anf && <Pill kind="accent">AnF: {anfLabel(v)}</Pill>}
    </span>
  );

  const print = () => {
    const head = `<tr><th>SAP</th><th>Vessel</th><th>Model</th><th>Assembly %</th><th>Delivery</th><th>AnF</th><th>Flags</th></tr>`;
    const body = rows.map(({ v, f }) => {
      const flags = [f.holds && 'Hold', f.overdue && 'Overdue', f.departing && 'Departing', f.anf && `AnF:${anfLabel(v)}`].filter(Boolean).join(', ');
      const anf = anfLabel(v) ?? '-';
      return `<tr><td>${esc(v.sap)}</td><td>${esc(v.name)}</td><td>${esc(v.model)}</td><td>${Math.round(assemblyPctNum(v))}%</td><td>${esc(fmt(v.deliveryDate) || v.deliveryDate || '-')}</td><td>${anf}</td><td>${flags}</td></tr>`;
    }).join('');
    const activeLabels = [...active].map((p) => PANEL_LABEL[p]).join(' · ') || 'none';
    const note = active.has('anf') ? `<div class="sub" style="color:#b45309;font-weight:600">⚠ Please cross-check with Sales’ Orders.</div>` : '';
    const opened = printSchedule('Pre-Delivery Alerts', `<h1>Pre-Delivery Alerts</h1><div class="sub">${esc(activeLabels)} · ${esc(fmt(new Date()))} · ${rows.length} vessels</div>${note}<table><thead>${head}</thead><tbody>${body}</tbody></table>`);
    setPrintErr(opened ? null : new Error('The print window was blocked. Allow pop-ups for this site and try again.'));
  };

  return (
    <>
      <PageHeader title="Pre-Delivery Alerts">
        <SearchInput value={q} onChange={setQ} placeholder="Search SAP / vessel / model" width={220} />
        <button className="btn btn-sm" onClick={print} disabled={rows.length === 0}><IconPrinter size={14} /> Print list</button>
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}><SyncLabel busy={isFetching} /></button>
      </PageHeader>
      {printErr && <InlineError error={printErr} onDismiss={() => setPrintErr(null)} />}

      {isLoading && <LoadingState label="Loading alerts…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && (
        <>
          {/* the headline: the four categories as one sentence; each figure shows or hides its boats below */}
          <FactSentence className="al-facts">
            Ahead of delivery there {counts.holds === 1 ? 'is' : 'are'} {factOf(facts[0]!)}<FactEnd>,</FactEnd>{' '}
            {factOf(facts[1]!)}<FactEnd>,</FactEnd>{' '}
            {factOf(facts[2]!)}{' '}
            and {factOf(facts[3]!)}<FactEnd>.</FactEnd>
          </FactSentence>
          <FactSentence sub className="al-facts-sub">
            {isPhone ? 'Tap' : 'Click'} a figure to show or hide those boats in the list.
          </FactSentence>

          <div className="page-panel al-panel">
            {/* the category toggles are the figures in the sentence above; the departing window and the AnF values refine them here */}
            <div className="toolbar" style={{ alignItems: 'center' }}>
              {active.has('departing') && (
                <label className="muted" style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
                  within
                  <input className="field" type="number" min={1} max={365} value={departRaw} onChange={(e) => setDepartRaw(e.target.value)} style={{ width: 62 }} />
                  days
                </label>
              )}
              {active.has('anf') && (
                <ChipGroup label="AnF">
                  <Chip active={anfVals.has('yes')} onClick={() => toggleAnf('yes')}>Yes</Chip>
                  <Chip active={anfVals.has('hold')} onClick={() => toggleAnf('hold')}>Hold</Chip>
                </ChipGroup>
              )}
              <span className="muted" style={{ marginLeft: 'auto' }}>{rows.length} vessel{rows.length === 1 ? '' : 's'}</span>
            </div>

            {active.has('anf') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '4px 0 12px', padding: '9px 12px', background: 'var(--warn-bg)', color: 'var(--warn-fg)', border: '1px solid var(--warn-bd)', borderRadius: 'var(--radius-lg)', fontSize: 12, fontWeight: 600 }}>
                <IconAlertTriangle size={14} /> AnF list is from Smartsheet - please cross-check with Sales’ Orders.
              </div>
            )}

            <div className="tbl-wrap">
              {/* phones: a card per vessel: name with the SAP at the end, "model · % · DEL" and the flags */}
              <table className="tbl tbl-cards">
                <thead>
                  <tr><th>SAP</th><th>Vessel</th><th>Model</th><th>Assembly %</th><th>Delivery</th><th>AnF</th><th>Flags</th></tr>
                </thead>
                <tbody>
                  {rows.map(({ v, f }) => {
                    const pct = Math.round(assemblyPctNum(v));
                    const del = parseDeliveryDate(v) ? fmt(v.deliveryDate) : v.deliveryDate || '-';
                    const anf = anfLabel(v);
                    return (
                      <tr key={v.rowId}>
                        <td className="tc-end al-sap">{v.sap}</td>
                        <td className="tc-title">{v.name}</td>
                        <td className="tc-hide">{v.model}</td>
                        <td className="tc-hide">{pct}%</td>
                        <td className="tc-hide">{del}</td>
                        <td className="tc-meta al-meta">{[v.model, `${pct}%`, `DEL ${del}`].filter(Boolean).join(' · ')}</td>
                        {/* on a card the AnF value shows only when the flag pills don't already carry it */}
                        <td className={anf && !f.anf ? 'tc-lbl' : 'tc-hide'} data-label="AnF">{anf ? <Pill kind={anf === 'Hold' ? 'warn' : 'ok'}>{anf}</Pill> : '-'}</td>
                        <td className="tc-full">{flagChips(f, v)}</td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                      {active.size === 0 ? 'Turn on a category above to see alerts.' : 'Nothing here - all clear.'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}
